// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { toArraySchema } from '@/api/schemas';
import { responseMetadataSchema } from '@/api/types';
import {
  inboxCountSchema,
  inboxListingSchema,
} from '@/notification/api/notification-types';
import { toDrawableMessages } from '@/notification/utilities/render-message';
import { z } from 'zod';

const INBOX_URL = '/v1/notifications/inbox';

const listingResponseSchema = z.object({
  data: inboxListingSchema,
  metadata: responseMetadataSchema,
});

const countResponseSchema = z.object({
  data: inboxCountSchema,
  metadata: responseMetadataSchema,
});

// Naming a message is not drawing it, so the cursor read asks for the key alone
// rather than a message the bundle has to understand.
const cursorResponseSchema = z.object({
  data: z.object({
    results: toArraySchema(z.object({ key: z.string() })),
  }),
  metadata: responseMetadataSchema,
});

/** Which messages a listing asks for. */
export type InboxState = 'unread' | 'read' | 'all';

export interface GetInboxInput {
  state: InboxState;
  page: number;
  limit: number;
  // A caller that draws titles only asks for a page without body templates
  isSparse?: boolean;
}

/** Reads one page of the caller's in-app messages, newest first. */
export async function getInbox(input: GetInboxInput) {
  const response = await api.get(INBOX_URL, {
    params: {
      state: input.state,
      page: input.page,
      limit: input.limit,
      sparse: input.isSparse ?? false,
    },
  });
  const result = await parseResponse(listingResponseSchema, response.data, {
    schemaName: 'NotificationInbox',
    endpoint: INBOX_URL,
  });
  const drawableMessages = toDrawableMessages(result.data.results);
  // The count is what the state matched, so it stands whether or not every
  // message in the page could be drawn
  return {
    ...result.data,
    results: drawableMessages,
    droppedMessageCount: result.data.results.length - drawableMessages.length,
  };
}

/**
 * Reads the key of the newest unread message, or nothing when none is unread.
 *
 * This is the bound for marking everything read, so it must not go through the
 * listing the screen draws: that one drops a message the bundle cannot draw,
 * and dropping the newest one here would report an empty inbox and clear the
 * badge over messages that are still unread.
 */
async function getNewestUnreadKey(): Promise<string | null> {
  const response = await api.get(INBOX_URL, {
    params: { state: 'unread', page: 1, limit: 1, sparse: true },
  });
  const result = await parseResponse(cursorResponseSchema, response.data, {
    schemaName: 'NotificationInboxCursor',
    endpoint: INBOX_URL,
  });
  return result.data.results[0]?.key ?? null;
}

/** Reads how many messages the caller has not read. */
export async function getUnreadCount() {
  const url = `${INBOX_URL}/unread-count`;
  const response = await api.get(url);
  const result = await parseResponse(countResponseSchema, response.data, {
    schemaName: 'NotificationUnreadCount',
    endpoint: url,
  });
  return result.data.unreadCount;
}

/** Marks a selection of messages read, and answers with the new unread count. */
export async function markMessagesRead(messageKeys: string[]) {
  return postForCount(`${INBOX_URL}/read`, 'MarkMessagesRead', { messageKeys });
}

/** Puts a selection of messages back unread, and answers with the new count. */
export async function markMessagesUnread(messageKeys: string[]) {
  return postForCount(`${INBOX_URL}/unread`, 'MarkMessagesUnread', {
    messageKeys,
  });
}

/**
 * Marks every unread message read, leaving one that arrives mid-request alone.
 *
 * The bound is the newest unread message rather than the newest on the page the
 * caller is looking at, because everything above it is already read. Reading the
 * key here rather than taking it from the rendered list is what keeps the answer
 * right on any page but the first.
 */
export async function markAllMessagesRead() {
  const lastSeenMessageKey = await getNewestUnreadKey();
  if (lastSeenMessageKey === null) {
    // Nothing is unread, so nothing needs marking and the badge is empty
    return { unreadCount: 0 };
  }
  const url = `${INBOX_URL}/read-all`;
  const response = await api.post(url, { lastSeenMessageKey });
  const result = await parseResponse(countResponseSchema, response.data, {
    schemaName: 'MarkAllMessagesRead',
    endpoint: url,
  });
  return result.data;
}

/** Posts to a route whose whole answer is the caller's new unread count. */
async function postForCount(url: string, schemaName: string, body?: unknown) {
  const response = await api.post(url, body);
  const result = await parseResponse(countResponseSchema, response.data, {
    schemaName,
    endpoint: url,
  });
  return result.data;
}
